import { useState, useEffect } from 'react';
import { Search, RefreshCw, ChevronDown, ChevronRight, User, Shield, Check, Clock, AlertCircle, X, XCircle, FileText, Database, DollarSign, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const KYC_STATUS = {
  PENDING: { label: 'KYC Pending', color: 'text-amber-400 bg-amber-500/20' },
  COMPLETED: { label: 'KYC Completed', color: 'text-emerald-400 bg-emerald-500/20' },
};

const INV_STATE_COLORS = {
  INTEREST: 'text-gray-400', EOI_PAID: 'text-blue-400', DOC_SENT: 'text-amber-400',
  DOC_SIGNED: 'text-purple-400', CAPITAL_PAID: 'text-emerald-400', COMPLETED: 'text-gray-300',
  CANCELLED: 'text-red-400', REFUNDED: 'text-orange-400',
};

const AUDIT_ACTION_COLORS = {
  REGISTERED: 'text-blue-400 bg-blue-500/20',
  KYC_STARTED: 'text-amber-400 bg-amber-500/20',
  KYC_SUBMITTED: 'text-amber-400 bg-amber-500/20',
  KYC_COMPLETED: 'text-emerald-400 bg-emerald-500/20',
  EOI_SUBMITTED: 'text-purple-400 bg-purple-500/20',
  EOI_PAID: 'text-blue-400 bg-blue-500/20',
  DOC_SENT: 'text-amber-400 bg-amber-500/20',
  DOC_SIGNED: 'text-purple-400 bg-purple-500/20',
  CAPITAL_PAID: 'text-emerald-400 bg-emerald-500/20',
  CANCELLED: 'text-red-400 bg-red-500/20',
  REFUNDED: 'text-orange-400 bg-orange-500/20',
};

const formatINR = (val) => {
  if (!val) return '₹0';
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)} L`;
  return `₹${(val / 1000).toFixed(0)} K`;
};

export default function AdminInvestorsTab() {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [stats, setStats] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (kycFilter) params.set('kyc_filter', kycFilter);
      if (search) params.set('search', search);
      const res = await api.get(`/admin/investors/consolidated?${params}`);
      setInvestors(res.data.investors || []);
      setStats(res.data.stats || {});
    } catch (err) {
      setError('Failed to load investors. Please try again.');
      setInvestors([]);
      toast.error('Failed to load investor data');
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [kycFilter]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const statCards = [
    { key: '', label: 'Total Investors', count: stats.total || 0, color: 'text-white', icon: User },
    { key: 'COMPLETED', label: 'KYC Completed', count: stats.kycCompleted || 0, color: 'text-emerald-400', icon: Check },
    { key: 'PENDING', label: 'KYC Pending', count: stats.kycPending || 0, color: 'text-amber-400', icon: Clock },
    { key: 'active', label: 'Active Investors', count: stats.activeInvestors || 0, color: 'text-blue-400', icon: TrendingUp },
    { key: 'invested', label: 'Total Invested', count: formatINR(stats.totalInvested || 0), color: 'text-[#4ADE80]', icon: DollarSign, isAmount: true },
  ];

  return (
    <div className="p-8" data-testid="investors-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl text-white font-light">Investor Management</h1>
          <p className="text-sm text-gray-500 mt-1">KYC tracking, investments, and activity overview</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchData} className="p-2 border border-white/10 text-gray-400 hover:text-white" data-testid="refresh-investors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-6">
        {statCards.map(s => {
          const Icon = s.icon;
          const isClickable = !s.isAmount && s.key !== 'active' && s.key !== 'invested';
          return (
            <button 
              key={s.key} 
              onClick={() => isClickable && setKycFilter(kycFilter === s.key ? '' : s.key)}
              className={`flex-1 p-4 border transition-all ${kycFilter === s.key ? 'border-white/30 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10'} ${!isClickable ? 'cursor-default' : ''}`}
              data-testid={`stat-${s.key || 'all'}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${s.color}`} />
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
              <p className={`text-xl font-mono ${s.color}`}>{s.count}</p>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6 flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white focus:outline-none focus:border-white/30"
            placeholder="Search by name or email..." data-testid="search-investors" />
        </div>
        <button type="submit" className="px-4 py-2 border border-white/10 text-sm text-gray-400 hover:text-white">Search</button>
      </form>

      {/* Investor list */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">Loading investors...</div>
      ) : investors.length === 0 ? (
        <div className="text-center py-16 text-gray-500">No investors found</div>
      ) : (
        <div className="space-y-2">
          {investors.map(inv => {
            const isExpanded = expanded[inv.userId];
            const kyc = inv.kyc || {};
            const kycCfg = KYC_STATUS[kyc.status] || KYC_STATUS.PENDING;

            return (
              <div key={inv.userId} className="border border-white/10" data-testid={`investor-row-${inv.userId}`}>
                {/* Header row */}
                <button onClick={() => toggle(inv.userId)}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors text-left">
                  <div className="flex items-center gap-4">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                    <div className="w-8 h-8 bg-white/10 flex items-center justify-center"><User className="w-4 h-4 text-gray-400" /></div>
                    <div>
                      <span className="text-sm text-white">{inv.name}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-gray-600">{inv.email}</p>
                        {inv.referredBy && <span className="text-xs text-gray-600">| via {typeof inv.referredBy === 'object' ? inv.referredBy.name : inv.referredBy}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className={`text-xs px-2.5 py-1 ${kycCfg.color}`}>{kycCfg.label}</span>
                    <div className="text-right">
                      <span className="text-sm font-mono text-white">{formatINR(inv.summary?.totalInvested)}</span>
                      <p className="text-xs text-gray-600">{inv.summary?.activeInvestments || 0} active · {inv.summary?.totalUnits || 0} units</p>
                    </div>
                  </div>
                </button>

                {/* Expanded: KYC + Investments + Audit — always shows all sections */}
                {isExpanded && (
                  <div className="border-t border-white/5 px-6 py-4 space-y-5 bg-white/[0.02]">
                    {/* Contact & Registration */}
                    <div className="grid grid-cols-4 gap-4 text-sm pb-4 border-b border-white/5">
                      <div>
                        <p className="text-xs text-gray-600">Phone</p>
                        <p className="text-white">{inv.phone || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Registered</p>
                        <p className="text-white">{inv.registeredAt ? new Date(inv.registeredAt).toLocaleDateString() : '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Referred By</p>
                        <p className="text-white">{typeof inv.referredBy === 'object' ? inv.referredBy.name : (inv.referredBy || 'Direct')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">User ID</p>
                        <p className="text-white font-mono text-xs">{inv.userId}</p>
                      </div>
                    </div>

                    {/* KYC Section — always visible */}
                    <div data-testid={`kyc-section-${inv.userId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5" /> KYC Details
                        <span className={`ml-auto text-xs px-2 py-0.5 ${kycCfg.color}`}>{kycCfg.label}</span>
                      </h3>
                      {kyc.status === 'COMPLETED' ? (
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-600">PAN</p>
                            <p className="text-white">{kyc.pan || '—'} {kyc.panVerified && <Check className="w-3 h-3 inline text-emerald-400" />}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Aadhaar</p>
                            <p className="text-white">{kyc.aadhaar ? `XXXX-${kyc.aadhaar}` : '—'} {kyc.aadhaarVerified && <Check className="w-3 h-3 inline text-emerald-400" />}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Bank</p>
                            <p className="text-white">{kyc.bankName || '—'} {kyc.bankAccount ? `***${kyc.bankAccount}` : ''} {kyc.bankVerified && <Check className="w-3 h-3 inline text-emerald-400" />}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">Address</p>
                            <p className="text-white truncate">{kyc.address || '—'}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 border border-amber-500/20 bg-amber-500/5">
                          <p className="text-sm text-amber-400">KYC incomplete — {kyc.pan ? 'PAN submitted, awaiting Aadhaar & Bank verification' : 'No documents submitted yet'}</p>
                        </div>
                      )}
                    </div>

                    {/* Investments — categorized by status */}
                    <div data-testid={`investments-section-${inv.userId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" /> Investment History
                        <span className="ml-auto text-xs text-gray-600">{inv.investments?.length || 0} investment{(inv.investments?.length || 0) !== 1 ? 's' : ''}</span>
                      </h3>
                      {inv.investments?.length > 0 ? (() => {
                        const PENDING_STATES = ['INTEREST', 'EOI_PAID', 'DOC_SENT', 'DOC_SIGNED', 'CAPITAL_PAID'];
                        const COMPLETED_STATES = ['COMPLETED'];
                        const REFUND_STATES = ['REFUNDED', 'REFUNDED_EOI', 'REFUNDED_FULL', 'CANCELLED'];
                        const pending = inv.investments.filter(i => PENDING_STATES.includes(i.state));
                        const completed = inv.investments.filter(i => COMPLETED_STATES.includes(i.state));
                        const refunded = inv.investments.filter(i => REFUND_STATES.includes(i.state));

                        const PENDING_ACTION = {
                          'INTEREST': { label: 'EOI Payment Due', color: 'text-gray-400 bg-gray-500/10' },
                          'EOI_PAID': { label: 'Documents Pending', color: 'text-amber-400 bg-amber-500/10' },
                          'DOC_SENT': { label: 'Awaiting Signature', color: 'text-amber-400 bg-amber-500/10' },
                          'DOC_SIGNED': { label: 'Capital Payment Due', color: 'text-orange-400 bg-orange-500/10' },
                          'CAPITAL_PAID': { label: 'Processing Completion', color: 'text-blue-400 bg-blue-500/10' },
                        };

                        const renderInvestmentRow = (investment) => (
                          <div key={investment.investmentId} className="flex items-center justify-between py-2.5 px-4 bg-white/5 text-sm border border-white/5">
                            <div className="flex items-center gap-3">
                              <span className="text-gray-500 font-mono text-xs w-16">{investment.investmentId}</span>
                              <span className="text-white">{investment.projectName ? `${investment.projectName} — ` : ''}{investment.unitName || investment.unitId}</span>
                              <span className={`text-xs px-2 py-0.5 border ${INV_STATE_COLORS[investment.state] || 'text-gray-400'} border-current/20`}>{investment.state?.replace(/_/g, ' ')}</span>
                              {PENDING_ACTION[investment.state] && (
                                <span className={`text-[10px] px-2 py-0.5 ${PENDING_ACTION[investment.state].color}`}>
                                  {PENDING_ACTION[investment.state].label}
                                </span>
                              )}
                              {REFUND_STATES.includes(investment.state) && (
                                <span className="text-[10px] px-2 py-0.5 text-red-400 bg-red-500/10">
                                  {investment.state === 'CANCELLED' ? 'Cancelled' : 'Refunded'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-5 text-xs">
                              <div className="text-right">
                                <span className="text-gray-500">EOI: </span>
                                <span className="font-mono text-white">{formatINR(investment.amounts?.eoiPaid)}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-gray-500">Capital: </span>
                                <span className="font-mono text-white">{formatINR(investment.amounts?.capitalPaid)}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-gray-500">Commitment: </span>
                                <span className="font-mono text-[#4ADE80]">{formatINR(investment.amounts?.totalCommitment)}</span>
                              </div>
                              <span className="text-gray-600 w-28 text-right">Broker: {investment.brokerName || '—'}</span>
                            </div>
                          </div>
                        );

                        return (
                          <div className="space-y-4">
                            {/* Pending Investments */}
                            {pending.length > 0 && (
                              <div>
                                <p className="text-[10px] text-amber-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                  <AlertCircle className="w-3 h-3" /> Pending Action ({pending.length})
                                </p>
                                <div className="space-y-1 border-l-2 border-amber-500/30 pl-0">
                                  {pending.map(renderInvestmentRow)}
                                </div>
                              </div>
                            )}

                            {/* Completed Investments */}
                            {completed.length > 0 && (
                              <div>
                                <p className="text-[10px] text-emerald-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                  <Check className="w-3 h-3" /> Completed ({completed.length})
                                </p>
                                <div className="space-y-1 border-l-2 border-emerald-500/30 pl-0">
                                  {completed.map(renderInvestmentRow)}
                                </div>
                              </div>
                            )}

                            {/* Refunded / Cancelled */}
                            {refunded.length > 0 && (
                              <div>
                                <p className="text-[10px] text-red-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                  <XCircle className="w-3 h-3" /> Refunded / Cancelled ({refunded.length})
                                </p>
                                <div className="space-y-1 border-l-2 border-red-500/30 pl-0">
                                  {refunded.map(renderInvestmentRow)}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })() : (
                        <div className="p-3 border border-white/5 bg-white/[0.02]">
                          <p className="text-sm text-gray-500">No investments yet</p>
                        </div>
                      )}
                    </div>

                    {/* Audit trail — always visible */}
                    <div data-testid={`audit-section-${inv.userId}`}>
                      <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" /> Audit Trail
                        <span className="ml-auto text-xs text-gray-600">{inv.auditTrail?.length || 0} events</span>
                      </h3>
                      {inv.auditTrail?.length > 0 ? (
                        <div className="space-y-0 border border-white/5 max-h-48 overflow-y-auto">
                          {inv.auditTrail.map((event, i) => (
                            <div key={i} className={`flex items-start gap-3 px-4 py-2.5 text-sm ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                              <span className="text-gray-600 text-xs w-32 flex-shrink-0 font-mono pt-0.5">
                                {event.timestamp ? new Date(event.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : ''}
                                {event.timestamp && <span className="text-gray-700 ml-1">{new Date(event.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
                              </span>
                              <span className={`text-xs px-2 py-0.5 flex-shrink-0 ${AUDIT_ACTION_COLORS[event.action] || 'text-gray-400 bg-white/5'}`}>
                                {event.action?.replace(/_/g, ' ')}
                              </span>
                              <span className="text-gray-400 text-xs flex-1">{event.details || event.reason || ''}</span>
                              {event.performedBy && <span className="text-gray-600 text-xs flex-shrink-0">by {typeof event.performedBy === 'object' ? event.performedBy.name : event.performedBy}</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-3 border border-white/5 bg-white/[0.02]">
                          <p className="text-sm text-gray-500">No activity recorded</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
